<template>
  <div class="mailing-container">
    <div class="card bg-white" style="border-radius: 1rem">
      <div class="card-body">
        <div class="mb-3">
          <h4 class="mb-1 d-flex align-items-center gap-2">
            <i class="fas fa-envelope text-success"></i>
            <span>Mailing</span>
          </h4>
          <p class="text-muted mb-0">
            Envíos puntuales por Excel o campañas automáticas con cola dinámica
            desde Firestore (Lun–Vie, franjas 12:00 / 15:00 / 18:00).
          </p>
        </div>

        <ul class="nav nav-pills gap-2 mb-3">
          <li class="nav-item">
            <button
              type="button"
              class="nav-link"
              :class="{ active: mailingTab === 'excel' }"
              @click="mailingTab = 'excel'"
            >
              Envío Excel
            </button>
          </li>
          <li class="nav-item">
            <button
              type="button"
              class="nav-link"
              :class="{ active: mailingTab === 'auto' }"
              @click="switchToAutoTab"
            >
              Campañas automáticas
            </button>
          </li>
        </ul>

        <div v-show="mailingTab === 'excel'">
        <div class="mb-3">
          <p class="text-muted mb-0 small">
            Sube un Excel (fila 1 = cabeceras) con
            <strong>Nombre</strong>, <strong>Email</strong> y
            <strong>emailNotifications</strong> (solo se envía si el valor es
            explícitamente <code>true</code> o <code>1</code>). Opcional:
            <strong>createdAt</strong> para ordenar de más antiguo a más nuevo.
            Indica un <strong>rango 1-based</strong> sobre esa lista (p. ej.
            1–100, 101–200). Cada fila de datos cuenta para el índice aunque el
            email sea inválido: no se envía, pero no se “recorta” la lista (así
            los rangos no se desplazan si corriges el Excel después).
          </p>
        </div>

        <hr class="my-3" />

        <div class="row g-3">
          <div class="col-12">
            <div class="row g-3 align-items-end">
              <div class="col-12 col-md-4">
                <label class="form-label">Plantilla</label>
                <select v-model="template" class="form-select">
                  <option value="closed_won">closed_won</option>
                  <option value="closed_lost">closed_lost</option>
                  <option value="ruleta_refiere">ruleta_refiere</option>
                  <option value="inactive_users">inactive_users</option>
                </select>
              </div>

              <div class="col-12 col-md-4">
                <label class="form-label">Asunto</label>
                <input
                  v-model="subject"
                  class="form-control"
                  type="text"
                  placeholder="Saludsa"
                />
              </div>

              <div class="col-12 col-md-4">
                <label class="form-label">Archivo Excel</label>
                <input
                  ref="fileInput"
                  class="form-control"
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  @change="onFileChange"
                />
                <small v-if="fileName" class="text-muted d-block mt-1">
                  {{ fileName }} ({{ prettyBytes(fileSize) }})
                </small>
              </div>

              <div class="col-12 col-md-3">
                <label class="form-label">Rango desde (1-based)</label>
                <input
                  v-model.number="rangeStart"
                  class="form-control"
                  type="number"
                  min="1"
                  :disabled="!canEditRange"
                  :max="maxRangeIndex"
                />
              </div>
              <div class="col-12 col-md-3">
                <label class="form-label">Rango hasta (inclusive)</label>
                <input
                  v-model.number="rangeEnd"
                  class="form-control"
                  type="number"
                  min="1"
                  :disabled="!canEditRange"
                  :max="maxRangeIndex"
                />
              </div>

              <div class="col-12 d-flex justify-content-md-end">
                <button
                  class="btn btn-success btn-sm"
                  :disabled="sending || !canSendMailing"
                  @click="send"
                >
                  <span
                    v-if="sending"
                    class="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {{ sending ? "Enviando..." : "Enviar" }}
                </button>
              </div>
            </div>

            <div
              v-if="error"
              class="tone-alert tone-alert--error mt-3"
              role="alert"
            >
              <div class="tone-alert__title">Error</div>
              <div class="tone-alert__body">{{ error }}</div>
            </div>

            <div
              v-if="excelStatsLoading"
              class="text-muted small mt-2"
              role="status"
            >
              Analizando Excel…
            </div>
            <div
              v-else-if="excelStatsError && excelBase64"
              class="tone-alert tone-alert--error mt-3"
              role="alert"
            >
              <div class="tone-alert__title">Excel</div>
              <div class="tone-alert__body">{{ excelStatsError }}</div>
            </div>
            <div
              v-else-if="excelStats && excelBase64"
              class="alert alert-light border mt-3 mb-0 py-2 small"
              role="region"
              aria-label="Resumen del archivo"
            >
              <strong>Lista ordenada:</strong>
              {{ excelStats.totalSortedRecipients }} filas de datos (cada una
              ocupa una posición en el rango).
              <span v-if="excelStats.hasCreatedAt" class="text-success">
                Orden por <code>createdAt</code>.
              </span>
              <span v-else class="text-muted">
                Sin columna <code>createdAt</code>: orden por fila del Excel.
              </span>
              <span v-if="excelStats.invalidRowsCount > 0" class="ms-1">
                Con email inválido o vacío (se omiten al enviar, pero cuentan en
                el rango): {{ excelStats.invalidRowsCount }}.
              </span>
              <div
                v-if="selectionIngresoCounts && rangeIsValid"
                class="mt-2"
              >
                <strong>Ingreso en el rango {{ rangeStart }}–{{ rangeEnd }}:</strong>
                Plataforma
                <span class="badge bg-secondary ms-1">{{
                  selectionIngresoCounts.Plataforma
                }}</span>
                · App
                <span class="badge bg-primary ms-1">{{
                  selectionIngresoCounts.App
                }}</span>
                · Vitality
                <span class="badge bg-info text-dark ms-1">{{
                  selectionIngresoCounts.Vitality
                }}</span>
                <span
                  v-if="!excelStats.hasIngresoColumn"
                  class="text-muted d-block mt-1"
                >
                  Sin columna <code>Ingreso</code> en el Excel: se infiere por
                  <code>registrationSource</code> del usuario (email).
                </span>
              </div>
              <div class="mt-1 text-muted">
                Solo se envía si el email es válido y
                <code>emailNotifications</code> es explícitamente
                <code>true</code> o <code>1</code>. Si el mismo email válido
                aparece dos veces en el rango, solo se envía una vez al segundo
                se omite como duplicado.
              </div>
            </div>

            <div v-if="result" class="mt-3">
              <div class="tone-alert tone-alert--success" role="alert">
                <div class="tone-alert__title">Enviado</div>
                <div class="tone-alert__body">
                  Mailing enviado correctamente.
                  <div class="mt-1">
                    <small class="text-muted">Log:</small>
                    <code>{{ result.mailingLogId }}</code>
                  </div>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-12 col-md-4 mb-3">
                  <div class="stat-card">
                    <div class="stat-icon">
                      <i class="fas fa-users text-primary"></i>
                    </div>
                    <div class="stat-content">
                      <h6 class="stat-value">{{ result.totalRecipients }}</h6>
                      <p class="stat-label">Correos enviados</p>
                    </div>
                  </div>
                </div>
                <div class="col-12 col-md-4 mb-3">
                  <div class="stat-card">
                    <div class="stat-icon">
                      <i class="fas fa-ban text-warning"></i>
                    </div>
                    <div class="stat-content">
                      <h6 class="stat-value">{{ result.invalidRowsCount }}</h6>
                      <p class="stat-label">Filas inválidas</p>
                    </div>
                  </div>
                </div>
                <div class="col-12 col-md-4 mb-3">
                  <div class="stat-card">
                    <div class="stat-icon">
                      <i class="fas fa-layer-group text-success"></i>
                    </div>
                    <div class="stat-content">
                      <h6 class="stat-value">
                        {{ (result.batches || []).length }}
                      </h6>
                      <p class="stat-label">Batches</p>
                    </div>
                  </div>
                </div>
                <div
                  v-if="result.skippedInvalidEmailCount != null"
                  class="col-12 col-md-4 mb-3"
                >
                  <div class="stat-card">
                    <div class="stat-icon">
                      <i class="fas fa-envelope-open-text text-warning"></i>
                    </div>
                    <div class="stat-content">
                      <h6 class="stat-value">
                        {{ result.skippedInvalidEmailCount }}
                      </h6>
                      <p class="stat-label">Omitidos (email inválido)</p>
                    </div>
                  </div>
                </div>
                <div
                  v-if="result.skippedEmailNotificationsCount != null"
                  class="col-12 col-md-4 mb-3"
                >
                  <div class="stat-card">
                    <div class="stat-icon">
                      <i class="fas fa-user-slash text-secondary"></i>
                    </div>
                    <div class="stat-content">
                      <h6 class="stat-value">
                        {{ result.skippedEmailNotificationsCount }}
                      </h6>
                      <p class="stat-label">Omitidos (notif. no true)</p>
                    </div>
                  </div>
                </div>
                <div
                  v-if="result.skippedDuplicateEmailCount != null"
                  class="col-12 col-md-4 mb-3"
                >
                  <div class="stat-card">
                    <div class="stat-icon">
                      <i class="fas fa-clone text-muted"></i>
                    </div>
                    <div class="stat-content">
                      <h6 class="stat-value">
                        {{ result.skippedDuplicateEmailCount }}
                      </h6>
                      <p class="stat-label">
                        Omitidos (email duplicado en rango)
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card mt-3" style="border-radius: 0.75rem">
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between"
                  >
                    <h6 class="mb-0">Respuesta</h6>
                    <button
                      class="btn btn-sm btn-outline-secondary"
                      @click="copyLog"
                    >
                      Copiar Log ID
                    </button>
                  </div>
                  <pre class="mt-2 mb-0 response-pre">{{
                    JSON.stringify(result, null, 2)
                  }}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-4 text-center">
          <div
            class="mb-2 d-flex align-items-center justify-content-center gap-2"
          >
            <h6 class="mb-0">Vista previa</h6>
            <small class="text-muted"
              >Plantilla: <code>{{ template }}</code></small
            >
          </div>
          <div class="preview-shell mx-auto">
            <template v-if="template === 'ruleta_refiere'">
              <iframe
                :key="'ruleta-preview'"
                class="preview-iframe"
                title="Vista previa Mail Ruleta Refiere"
                :src="ruletaPreviewUrl"
              />
            </template>
            <template v-else>
              <img
                v-for="(src, idx) in templatePreviewImages"
                :key="`${template}-${idx}`"
                :src="src"
                class="preview-img"
                alt=""
                loading="lazy"
              />
              <div class="preview-footer">
                Si no deseas seguir recibiendo estos correos, puedes
                <a href="#" class="preview-unsub" @click.prevent
                  >darte de baja aquí</a
                >.
              </div>
            </template>
          </div>
          <small class="text-muted d-block mt-2">
            <template v-if="template === 'ruleta_refiere'">
              Misma plantilla que envía el backend
              (<code>ruletaRefiereEmail.ts</code>). Vista estática: «aquí» usa
              <code>#</code>; en el envío real, URL con token.
            </template>
            <template v-else>
              La vista previa usa las mismas imágenes que el correo.
            </template>
          </small>
        </div>

        <hr class="my-4" />

        <div class="mt-2">
          <div
            class="d-flex align-items-center justify-content-between flex-wrap gap-2"
          >
            <div>
              <h6 class="mb-1">Campañas recientes</h6>
              <small class="text-muted"
                >Historial de campañas (columna <strong>Rango Excel</strong>:
                valores «desde–hasta» del envío y total de filas del archivo) y
                acceso a análisis.</small
              >
            </div>
            <button
              class="btn btn-sm btn-outline-primary"
              :disabled="loadingCampaigns"
              @click="fetchCampaigns"
            >
              <span
                v-if="loadingCampaigns"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {{ loadingCampaigns ? "Actualizando..." : "Actualizar" }}
            </button>
          </div>

          <div
            v-if="campaignsError"
            class="tone-alert tone-alert--error mt-3"
            role="alert"
          >
            <div class="tone-alert__title">Error</div>
            <div class="tone-alert__body">{{ campaignsError }}</div>
          </div>

          <div class="table-responsive mt-3">
            <table class="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Plantilla</th>
                  <th>Asunto</th>
                  <th>Rango Excel</th>
                  <th>Estado</th>
                  <th class="text-end">Dest.</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!loadingCampaigns && campaigns.length === 0">
                  <td colspan="7" class="text-muted">
                    No hay campañas para mostrar.
                  </td>
                </tr>
                <tr v-for="c in campaigns" :key="c.id">
                  <td>
                    <div class="small fw-semibold">
                      {{ prettyDate(c.finishedAt || c.createdAt) }}
                    </div>
                    <div class="small text-muted">
                      <code>{{ c.id }}</code>
                    </div>
                  </td>
                  <td>
                    <code>{{ c.template || "-" }}</code>
                  </td>
                  <td class="text-truncate" style="max-width: 320px">
                    {{ c.subject || "-" }}
                  </td>
                  <td class="small text-nowrap">
                    <span
                      v-if="campaignExcelRangeLabel(c)"
                      class="fw-semibold"
                      >{{ campaignExcelRangeLabel(c) }}</span
                    >
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <span class="badge" :class="statusBadgeClass(c.status)">
                      {{ c.status || "-" }}
                    </span>
                  </td>
                  <td class="text-end">{{ c.totalRecipients ?? "-" }}</td>
                  <td class="text-end">
                    <button
                      class="btn btn-sm btn-outline-success"
                      @click="openAnalysis(c)"
                    >
                      Análisis
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div
          v-if="analysisOpen"
          class="analysis-modal-backdrop"
          @click.self="closeAnalysis"
        >
          <div class="analysis-modal">
            <div class="d-flex align-items-start justify-content-between gap-2">
              <div>
                <h6 class="mb-1">Análisis de campaña</h6>
                <div class="small text-muted">
                  Log: <code>{{ selectedCampaign?.id }}</code>
                </div>
                <div
                  v-if="campaignExcelRangeLabel(selectedCampaign)"
                  class="small text-muted"
                >
                  Rango Excel:
                  <strong>{{
                    campaignExcelRangeLabel(selectedCampaign)
                  }}</strong>
                </div>
                <div v-if="latestSnapshotId" class="small text-muted">
                  Snapshot día: <code>{{ latestSnapshotId }}</code>
                  <span v-if="latestSnapshotAt">
                    · Última actualización:
                    <strong>{{ prettyDate(latestSnapshotAt) }}</strong>
                  </span>
                </div>
              </div>
              <div class="d-flex align-items-center gap-2">
                <button
                  class="btn btn-sm btn-outline-primary"
                  :disabled="analysisLoading || recalculating"
                  @click="recalculateAnalysis"
                  title="Recalcular y guardar snapshot del día (si aplica)"
                >
                  <span
                    v-if="recalculating"
                    class="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {{ recalculating ? "Recalculando..." : "Recalcular ahora" }}
                </button>
                <button
                  class="btn btn-sm btn-outline-secondary"
                  @click="closeAnalysis"
                >
                  Cerrar
                </button>
              </div>
            </div>

            <div
              v-if="analysisError"
              class="tone-alert tone-alert--error mt-3"
              role="alert"
            >
              <div class="tone-alert__title">Error</div>
              <div class="tone-alert__body">{{ analysisError }}</div>
            </div>

            <div v-if="analysisLoading" class="mt-3 small text-muted">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Cargando análisis...
            </div>

            <div
              v-if="!analysis && !analysisLoading && !analysisError"
              class="mt-3"
            >
              <div class="tone-alert tone-alert--info" role="alert">
                <div class="tone-alert__title">Aún no hay datos guardados</div>
                <div class="tone-alert__body">
                  Esta campaña todavía no tiene snapshots. Se generarán
                  automáticamente (cada 4 horas) durante los primeros 3 días o
                  puedes usar
                  <strong>Recalcular ahora</strong>.
                </div>
              </div>
            </div>

            <div v-if="analysis && !analysisLoading" class="mt-3">
              <div class="row g-3">
                <div class="col-12 col-md-4">
                  <div class="stat-card">
                    <div class="stat-icon">
                      <i class="fas fa-envelope text-success"></i>
                    </div>
                    <div class="stat-content">
                      <h6 class="stat-value">
                        {{ analysis.totalRecipients ?? "-" }}
                      </h6>
                      <p class="stat-label">Destinatarios (válidos)</p>
                    </div>
                  </div>
                </div>
                <div class="col-12 col-md-4">
                  <div class="stat-card">
                    <div class="stat-icon">
                      <i class="fas fa-user-check text-primary"></i>
                    </div>
                    <div class="stat-content">
                      <h6 class="stat-value">
                        {{ analysis.matchedUsersCount ?? 0 }}
                      </h6>
                      <p class="stat-label">Usuarios matcheados</p>
                    </div>
                  </div>
                </div>
                <div class="col-12 col-md-4">
                  <div class="stat-card">
                    <div class="stat-icon">
                      <i class="fas fa-calendar-day text-warning"></i>
                    </div>
                    <div class="stat-content">
                      <h6 class="stat-value">
                        {{ prettyDate(analysis.finishedAt) }}
                      </h6>
                      <p class="stat-label">Fecha envío (finishedAt)</p>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="analysis.lastLogin"
                class="card mt-3"
                style="border-radius: 0.75rem"
              >
                <div class="card-body">
                  <h6 class="mb-2">
                    <i class="fas fa-sign-in-alt text-primary me-1"></i>
                    Inicios (proxy <code>lastLogin</code>)
                  </h6>
                  <p class="small text-muted mb-2">
                    {{ analysis.lastLogin.note }}
                  </p>
                  <div class="d-flex align-items-center flex-wrap gap-3">
                    <div
                      class="fw-bold"
                      :class="
                        analysis.lastLogin.increased
                          ? 'text-success'
                          : 'text-muted'
                      "
                    >
                      {{
                        analysis.lastLogin.increased
                          ? "Sí: subió respecto al baseline"
                          : "No: no subió (o igual)"
                      }}
                    </div>
                    <div class="small text-muted">
                      Post (D0–D3 hasta hoy):
                      <strong>{{
                        analysis.lastLogin.inPostWindow ?? 0
                      }}</strong>
                      · Baseline (4 días pre):
                      <strong>{{
                        analysis.lastLogin.inBaselineWindow ?? 0
                      }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card mt-3" style="border-radius: 0.75rem">
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between flex-wrap gap-2"
                  >
                    <h6 class="mb-0">Impacto (leads después del envío)</h6>
                    <small class="text-muted"
                      >TZ: <code>{{ analysis.tz }}</code></small
                    >
                  </div>

                  <div class="table-responsive mt-2">
                    <table class="table table-sm">
                      <thead>
                        <tr>
                          <th>Día post-envío</th>
                          <th class="text-end">Leads post-envío</th>
                          <th>Día baseline (pareado)</th>
                          <th class="text-end">Leads baseline</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in analysisRows" :key="row.dayKey">
                          <td class="impact-td">
                            <code>{{ row.dayKey }}</code>
                          </td>
                          <td class="text-end fw-semibold impact-td">
                            {{ row.after }}
                          </td>
                          <td class="impact-td">
                            <code>{{ row.baselineDayKey }}</code>
                          </td>
                          <td class="text-end impact-td">
                            {{ row.baseline }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div
                v-if="analysis.leadCreatorsPostSend !== undefined"
                class="card mt-3"
                style="border-radius: 0.75rem"
              >
                <div class="card-body">
                  <h6 class="mb-2">
                    <i class="fas fa-user-plus text-success me-1"></i>
                    Referidores con leads (post-envío)
                  </h6>
                  <p class="small text-muted mb-2">
                    {{ analysis.leadCreatorsPostSendNote }}
                  </p>
                  <p
                    v-if="analysis.leadCreatorsPostSendWindow"
                    class="small text-muted mb-2"
                  >
                    Ventana UTC (referencia):
                    <code>{{ analysis.leadCreatorsPostSendWindow.start }}</code>
                    →
                    <code>{{
                      analysis.leadCreatorsPostSendWindow.endExclusive
                    }}</code>
                  </p>
                  <div
                    v-if="analysis.leadCreatorsPostSendTruncated"
                    class="alert alert-warning py-2 small mb-2"
                    role="status"
                  >
                    Mostrando las primeras filas ordenadas por volumen ({{
                      (analysis.leadCreatorsPostSend || []).length
                    }}
                    de {{ analysis.leadCreatorsPostSendTotal }} referidores con
                    leads).
                  </div>
                  <div
                    v-if="!(analysis.leadCreatorsPostSend || []).length"
                    class="small text-muted mb-0"
                  >
                    Ningún referidor de la campaña (con
                    <code>userId</code> enlazado en el envío) registró leads en
                    esta ventana.
                  </div>
                  <div v-else class="table-responsive lead-creators-table">
                    <table class="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Email</th>
                          <th>userId</th>
                          <th class="text-end">Leads</th>
                          <th>Primer lead</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(row, idx) in analysis.leadCreatorsPostSend"
                          :key="`${row.userId}-${idx}`"
                        >
                          <td>{{ row.displayName || "—" }}</td>
                          <td class="small text-break">
                            {{ row.email || "—" }}
                          </td>
                          <td>
                            <code class="small">{{ row.userId }}</code>
                          </td>
                          <td class="text-end fw-semibold">
                            {{ row.leadCount }}
                          </td>
                          <td class="small">
                            {{
                              row.firstLeadAt
                                ? prettyDate(row.firstLeadAt)
                                : "—"
                            }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div class="card mt-3" style="border-radius: 0.75rem">
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between flex-wrap gap-2"
                  >
                    <h6 class="mb-0">Historial de snapshots</h6>
                    <small class="text-muted"
                      >{{ (snapshots || []).length }} día(s) con corte
                      guardado</small
                    >
                  </div>
                  <p class="small text-muted mt-2 mb-3">
                    Cada fila es un día (documento en Firestore). La vista
                    principal arriba usa el <strong>día más reciente</strong> de
                    esta lista.
                  </p>
                  <div
                    v-if="(snapshots || []).length === 0"
                    class="small text-muted"
                  >
                    Sin snapshots todavía.
                  </div>
                  <div v-else class="table-responsive">
                    <table class="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Día (doc)</th>
                          <th class="text-end">D+N</th>
                          <th>Última actualización</th>
                          <th>Creado</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="sr in snapshotTableRows" :key="sr.id">
                          <td>
                            <code>{{ sr.id }}</code>
                          </td>
                          <td class="text-end text-muted">
                            <template v-if="sr.dayOffset === '—'">—</template>
                            <template v-else>D+{{ sr.dayOffset }}</template>
                          </td>
                          <td class="small">{{ sr.updated }}</td>
                          <td class="small text-muted">{{ sr.created }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <details class="mt-3">
                    <summary class="small text-muted user-select-none">
                      JSON técnico (debug)
                    </summary>
                    <pre class="mt-2 mb-0 response-pre response-pre--compact">{{
                      JSON.stringify({ analysis, snapshots }, null, 2)
                    }}</pre>
                  </details>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
        <!-- /mailingTab excel -->

        <div v-show="mailingTab === 'auto'">
          <div class="mb-3">
            <h6 class="mb-1">Campañas automáticas</h6>
            <small class="text-muted">
              Cola dinámica desde Firestore. Al crear, la audiencia se arma en
              segundo plano (puede tardar con bases grandes); luego Lun–Vie
              06:00 y envíos 12:00 / 15:00 / 18:00 (America/Guayaquil), 200 por
              franja. Solo usuarios con
              <code>emailNotifications === true</code>, excepto audiencia
              <strong>Testing</strong> (<code>mailingSettings/testAudience</code>).
            </small>
          </div>

          <div class="row g-3 align-items-end">
            <div class="col-12 col-md-3">
              <label class="form-label">Audiencia</label>
              <select v-model="autoFilterType" class="form-select">
                <option value="all">Toda la base</option>
                <option value="referred_success">Referidores con éxito</option>
                <option value="referred_only_failed">
                  Referidores solo sin éxito
                </option>
                <option value="never_referred">Nunca han referido</option>
                <option value="testing">Testing (equipo interno)</option>
              </select>
            </div>
            <div class="col-12 col-md-3">
              <label class="form-label">Plantilla</label>
              <select v-model="autoTemplate" class="form-select">
                <option value="closed_won">closed_won</option>
                <option value="closed_lost">closed_lost</option>
                <option value="ruleta_refiere">ruleta_refiere</option>
                <option value="inactive_users">inactive_users</option>
              </select>
            </div>
            <div class="col-12 col-md-4">
              <label class="form-label">Asunto</label>
              <input
                v-model="autoSubject"
                class="form-control"
                type="text"
                placeholder="Saludsa"
              />
            </div>
            <div class="col-12 col-md-2 d-flex justify-content-md-end">
              <button
                class="btn btn-success btn-sm w-100"
                :disabled="autoCreating"
                @click="createAutoCampaign"
              >
                <span
                  v-if="autoCreating"
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {{ autoCreating ? "Creando..." : "Iniciar campaña" }}
              </button>
            </div>
          </div>

          <div
            v-if="autoCreateError"
            class="tone-alert tone-alert--error mt-3"
            role="alert"
          >
            <div class="tone-alert__title">Error</div>
            <div class="tone-alert__body">{{ autoCreateError }}</div>
          </div>

          <hr class="my-4" />

          <div
            class="d-flex align-items-center justify-content-between flex-wrap gap-2"
          >
            <div>
              <h6 class="mb-1">Campañas en curso</h6>
              <small class="text-muted"
                >Progreso = enviados / (enviados + pendientes).</small
              >
            </div>
            <button
              class="btn btn-sm btn-outline-primary"
              :disabled="loadingAutoCampaigns"
              @click="fetchAutoCampaigns"
            >
              <span
                v-if="loadingAutoCampaigns"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {{ loadingAutoCampaigns ? "Actualizando..." : "Actualizar" }}
            </button>
          </div>

          <div
            v-if="autoCampaignsError"
            class="tone-alert tone-alert--error mt-3"
            role="alert"
          >
            <div class="tone-alert__title">Error</div>
            <div class="tone-alert__body">{{ autoCampaignsError }}</div>
          </div>

          <div class="table-responsive mt-3">
            <table class="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Creada</th>
                  <th>Audiencia</th>
                  <th>Plantilla</th>
                  <th>Asunto</th>
                  <th>Progreso</th>
                  <th>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-if="!loadingAutoCampaigns && autoCampaigns.length === 0"
                >
                  <td colspan="7" class="text-muted">
                    No hay campañas automáticas.
                  </td>
                </tr>
                <tr v-for="c in autoCampaigns" :key="c.id">
                  <td>
                    <div class="small fw-semibold">
                      {{ prettyDate(c.createdAt) }}
                    </div>
                    <div class="small text-muted">
                      <code>{{ c.id }}</code>
                    </div>
                  </td>
                  <td class="small">{{ autoFilterLabel(c.filterType) }}</td>
                  <td>
                    <code>{{ c.template || "-" }}</code>
                  </td>
                  <td class="text-truncate" style="max-width: 240px">
                    {{ c.subject || "-" }}
                  </td>
                  <td style="min-width: 160px">
                    <div class="small mb-1">
                      {{ autoProgressLabel(c) }}
                    </div>
                    <div class="progress" style="height: 6px">
                      <div
                        class="progress-bar bg-success"
                        role="progressbar"
                        :style="{ width: autoProgressPct(c) + '%' }"
                        :aria-valuenow="autoProgressPct(c)"
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                  </td>
                  <td>
                    <span
                      class="badge"
                      :class="autoStatusBadgeClass(c.status)"
                    >
                      {{ c.status || "-" }}
                    </span>
                  </td>
                  <td class="text-end text-nowrap">
                    <button
                      class="btn btn-sm btn-outline-success me-1"
                      @click="openAutoDetail(c)"
                    >
                      Detalle
                    </button>
                    <button
                      v-if="c.status === 'active'"
                      class="btn btn-sm btn-outline-warning"
                      :disabled="autoStatusBusyId === c.id"
                      @click="pauseAutoCampaign(c)"
                    >
                      Pausar
                    </button>
                    <button
                      v-else-if="c.status === 'paused'"
                      class="btn btn-sm btn-outline-primary"
                      :disabled="autoStatusBusyId === c.id"
                      @click="resumeAutoCampaign(c)"
                    >
                      Reanudar
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            v-if="autoDetailOpen && selectedAutoCampaign"
            class="mt-4 border rounded p-3 bg-light"
          >
            <div
              class="d-flex align-items-start justify-content-between flex-wrap gap-2"
            >
              <div>
                <h6 class="mb-1">Detalle de campaña</h6>
                <small class="text-muted"
                  ><code>{{ selectedAutoCampaign.id }}</code></small
                >
              </div>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-sm btn-outline-primary"
                  :disabled="autoSnapshotBusy"
                  @click="runAutoSnapshotNow"
                >
                  <span
                    v-if="autoSnapshotBusy"
                    class="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {{
                    autoSnapshotBusy
                      ? "Ejecutando..."
                      : "Ejecutar snapshot ahora"
                  }}
                </button>
                <button
                  class="btn btn-sm btn-outline-secondary"
                  @click="closeAutoDetail"
                >
                  Cerrar
                </button>
              </div>
            </div>

            <div
              v-if="autoDetailError"
              class="tone-alert tone-alert--error mt-3"
              role="alert"
            >
              <div class="tone-alert__title">Error</div>
              <div class="tone-alert__body">{{ autoDetailError }}</div>
            </div>

            <div v-if="autoDetailLoading" class="text-muted small mt-3">
              Cargando detalle…
            </div>
            <div v-else-if="autoDetail" class="mt-3">
              <div
                v-if="!autoDetail.campaign?.stats?.lastSnapshotAt"
                class="alert alert-info py-2 small mb-3"
                role="status"
              >
                Construyendo audiencia en segundo plano… Los envíos empiezan
                cuando el snapshot termine. Esta vista se actualiza sola.
              </div>
              <div class="row g-3 small">
                <div class="col-6 col-md-3">
                  <div class="text-muted">Pending</div>
                  <div class="fw-semibold">
                    {{ autoDetail.recipientCounts?.pending ?? 0 }}
                  </div>
                </div>
                <div class="col-6 col-md-3">
                  <div class="text-muted">Sending</div>
                  <div class="fw-semibold">
                    {{ autoDetail.recipientCounts?.sending ?? 0 }}
                  </div>
                </div>
                <div class="col-6 col-md-3">
                  <div class="text-muted">Sent</div>
                  <div class="fw-semibold">
                    {{ autoDetail.recipientCounts?.sent ?? 0 }}
                  </div>
                </div>
                <div class="col-6 col-md-3">
                  <div class="text-muted">Failed</div>
                  <div class="fw-semibold">
                    {{ autoDetail.recipientCounts?.failed ?? 0 }}
                  </div>
                </div>
                <div class="col-6 col-md-3">
                  <div class="text-muted">Último snapshot</div>
                  <div class="fw-semibold">
                    {{
                      prettyDate(
                        autoDetail.campaign?.stats?.lastSnapshotAt,
                      )
                    }}
                  </div>
                </div>
              </div>
              <div
                v-if="autoSnapshotResult"
                class="alert alert-light border mt-3 mb-0 py-2 small"
              >
                Snapshot:
                añadidos {{ autoSnapshotResult.added ?? 0 }}, ya en cola
                {{ autoSnapshotResult.skippedExisting ?? 0 }}, audiencia
                {{ autoSnapshotResult.audienceSize ?? 0 }}
                <span v-if="autoSnapshotResult.error" class="text-danger">
                  — {{ autoSnapshotResult.error }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <!-- /mailingTab auto -->

        <hr class="my-4" />

        <div class="mailing-test-send border rounded p-3">
          <div class="d-flex align-items-start gap-2 mb-2">
            <i class="fas fa-exclamation-triangle text-warning mt-1"></i>
            <div>
              <h6 class="mb-1">Modo de prueba — envío real</h6>
              <p class="small mb-0 text-muted">
                Esto envía correos reales a través de la API de Saludsa. Úsalo
                solo con tus propios correos o de otros internos, nunca con la
                base de usuarios.
              </p>
            </div>
          </div>

          <div class="row g-3 align-items-end mt-1">
            <div class="col-12">
              <label class="form-label"
                >Emails de prueba
                <span class="text-muted fw-normal"
                  >(máx. 5, separados por coma o salto de línea)</span
                ></label
              >
              <textarea
                v-model="testSendEmailsRaw"
                class="form-control"
                rows="3"
                placeholder="tu@saludsa.com&#10;otro@planproec.com"
              ></textarea>
              <small class="text-muted"
                >Detectados: {{ testSendParsedEmails.length }} /
                5</small
              >
            </div>
            <div class="col-12 col-md-4">
              <label class="form-label">Plantilla</label>
              <select v-model="testSendTemplate" class="form-select">
                <option value="closed_won">closed_won</option>
                <option value="closed_lost">closed_lost</option>
                <option value="ruleta_refiere">ruleta_refiere</option>
                <option value="inactive_users">inactive_users</option>
              </select>
            </div>
            <div class="col-12 col-md-5">
              <label class="form-label">Asunto</label>
              <input
                v-model="testSendSubject"
                class="form-control"
                type="text"
                placeholder="Saludsa"
              />
            </div>
            <div class="col-12 col-md-3 d-flex justify-content-md-end">
              <button
                class="btn btn-warning btn-sm w-100"
                :disabled="testSendBusy || !canTestSend"
                @click="confirmAndSendTest"
              >
                <span
                  v-if="testSendBusy"
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {{ testSendBusy ? "Enviando..." : "Enviar prueba" }}
              </button>
            </div>
          </div>

          <div
            v-if="testSendError"
            class="tone-alert tone-alert--error mt-3"
            role="alert"
          >
            <div class="tone-alert__title">Error</div>
            <div class="tone-alert__body">{{ testSendError }}</div>
          </div>

          <div
            v-if="testSendResult"
            class="tone-alert tone-alert--success mt-3"
            role="alert"
          >
            <div class="tone-alert__title">Resultado de prueba</div>
            <div class="tone-alert__body">
              {{ testSendResult.message }}
              <span v-if="testSendResult.testSendId" class="ms-1">
                Log: <code>{{ testSendResult.testSendId }}</code>
              </span>
            </div>
          </div>

          <div
            v-if="testSendResult && (testSendResult.results || []).length"
            class="mt-3"
          >
            <div class="row g-2">
              <div
                v-for="(r, idx) in testSendResult.results"
                :key="`${r.email}-${idx}`"
                class="col-12 col-md-6"
              >
                <div
                  class="border rounded p-2 small bg-white"
                  :class="
                    r.ok
                      ? 'border-success'
                      : 'border-danger'
                  "
                >
                  <div class="d-flex justify-content-between gap-2">
                    <strong class="text-truncate">{{ r.email }}</strong>
                    <span
                      class="badge"
                      :class="r.ok ? 'bg-success' : 'bg-danger'"
                      >{{ r.ok ? "ok" : "error" }}</span
                    >
                  </div>
                  <div class="text-muted mt-1">
                    ingreso: <code>{{ r.ingreso || "-" }}</code>
                    <span v-if="r.status != null" class="ms-2"
                      >status: {{ r.status }}</span
                    >
                  </div>
                  <div v-if="r.detail" class="text-muted mt-1 text-break">
                    {{ r.detail }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: "MailingAdmin",
  props: {
    tokenAuth: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      mailingTab: "excel",

      template: "closed_won",
      subject: "Saludsa",
      excelBase64: "",
      fileName: "",
      fileSize: 0,
      excelStats: null,
      excelStatsLoading: false,
      excelStatsError: "",
      rangeStart: 1,
      rangeEnd: 1,
      sending: false,
      error: "",
      result: null,
      role: localStorage.getItem("session"),

      campaigns: [],
      loadingCampaigns: false,
      campaignsError: "",

      analysisOpen: false,
      selectedCampaign: null,
      analysisLoading: false,
      recalculating: false,
      analysisError: "",
      analysis: null,
      snapshots: [],
      latestSnapshotId: null,
      latestSnapshotAt: null,

      autoFilterType: "never_referred",
      autoTemplate: "closed_won",
      autoSubject: "Saludsa",
      autoCreating: false,
      autoCreateError: "",
      autoCampaigns: [],
      loadingAutoCampaigns: false,
      autoCampaignsError: "",
      autoStatusBusyId: "",
      autoDetailOpen: false,
      selectedAutoCampaign: null,
      autoDetailLoading: false,
      autoDetailError: "",
      autoDetail: null,
      autoSnapshotBusy: false,
      autoSnapshotResult: null,

      testSendEmailsRaw: "",
      testSendTemplate: "closed_won",
      testSendSubject: "Saludsa [PRUEBA]",
      testSendBusy: false,
      testSendError: "",
      testSendResult: null,
    };
  },
  computed: {
    ruletaPreviewUrl() {
      const base = process.env.BASE_URL || "/";
      const withSlash = base.endsWith("/") ? base : `${base}/`;
      return `${withSlash}mail/Mail_Ruleta_Refiere.html`;
    },
    templatePreviewImages() {
      if (this.template === "closed_won") {
        return [
          "https://firebasestorage.googleapis.com/v0/b/saludsapro.appspot.com/o/mail%2Fclosed_won%2FSALUDSAMesa%20de%20trabajo%201.png?alt=media&token=ba357245-0af7-4e65-9c93-d82be5a683e0",
          "https://firebasestorage.googleapis.com/v0/b/saludsapro.appspot.com/o/mail%2Fclosed_won%2FSALUDSA%20MAILING-03.png?alt=media&token=ec993a74-d6d2-4c23-8557-36930f0780cd",
          "https://firebasestorage.googleapis.com/v0/b/saludsapro.appspot.com/o/mail%2Fclosed_won%2FSALUDSAMesa%20de%20trabajo%205.png?alt=media&token=5131dcd1-c1a0-4777-a990-1223fe5d7649",
        ];
      }
      if (this.template === "inactive_users") {
        return [
          "https://firebasestorage.googleapis.com/v0/b/saludsapro.appspot.com/o/mail%2Finactive_users%2FSALUDSA%20MAILING-07%202.png?alt=media&token=1d0e190f-5491-4b6d-a293-afad4cc4a64e",
          "https://firebasestorage.googleapis.com/v0/b/saludsapro.appspot.com/o/mail%2Finactive_users%2FSALUDSA%20MAILING-09%202.png?alt=media&token=b9dccc27-801f-464c-9791-06f57cfc7821",
          "https://firebasestorage.googleapis.com/v0/b/saludsapro.appspot.com/o/mail%2Finactive_users%2FSALUDSA%20MAILING-08%202.png?alt=media&token=081519bf-61dd-4c5b-8f24-8fa88cae9ec3",
        ];
      }
      if (this.template === "closed_lost") {
        return [
          "https://firebasestorage.googleapis.com/v0/b/saludsapro.appspot.com/o/mail%2Fclosed_lost%2FSALUDSAMesa%20de%20trabajo%203.png?alt=media&token=f7897173-dbad-4224-9762-fb8da29636a1",
          "https://firebasestorage.googleapis.com/v0/b/saludsapro.appspot.com/o/mail%2Fclosed_lost%2FSALUDSAMesa%20de%20trabajo%207.png?alt=media&token=aefe455a-3687-47cd-a428-8b215e480d59",
          "https://firebasestorage.googleapis.com/v0/b/saludsapro.appspot.com/o/mail%2Fclosed_lost%2FSALUDSAMesa%20de%20trabajo%206.png?alt=media&token=4580915b-e9f9-455f-9940-eaf9e7490243",
        ];
      }
      if (this.template === "ruleta_refiere") {
        return [
          "https://firebasestorage.googleapis.com/v0/b/saludsapro.appspot.com/o/mail%2Fruleta_refiere%2FSALUDSAMesa%20de%20trabajo%201.png?alt=media&token=ba357245-0af7-4e65-9c93-d82be5a683e0",
          "https://firebasestorage.googleapis.com/v0/b/saludsapro.appspot.com/o/mail%2Fruleta_refiere%2FSALUDSAMesa%20de%20trabajo%205.png?alt=media&token=5131dcd1-c1a0-4777-a990-1223fe5d7649",
          "https://firebasestorage.googleapis.com/v0/b/saludsapro.appspot.com/o/mail%2Fruleta_refiere%2FSALUDSAMesa%20de%20trabajo%206.png?alt=media&token=4580915b-e9f9-455f-9940-eaf9e7490243",
        ];
      }
    },
    maxRangeIndex() {
      const n = Number(this.excelStats?.totalSortedRecipients);
      return Number.isFinite(n) && n >= 1 ? n : 1;
    },
    canEditRange() {
      return (
        !!this.excelStats &&
        this.excelStats.code === 200 &&
        !this.excelStatsLoading
      );
    },
    rangeIsValid() {
      const t = Number(this.excelStats?.totalSortedRecipients);
      if (!Number.isFinite(t) || t < 1) return false;
      const a = Math.floor(Number(this.rangeStart));
      const b = Math.floor(Number(this.rangeEnd));
      if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
      if (a < 1 || b < 1 || a > b) return false;
      if (b > t) return false;
      return true;
    },
    canSendMailing() {
      return (
        !!this.excelBase64 &&
        !this.excelStatsLoading &&
        !this.excelStatsError &&
        this.excelStats &&
        this.excelStats.code === 200 &&
        this.rangeIsValid &&
        !this.sending
      );
    },
    selectionIngresoCounts() {
      const labels = this.excelStats?.ingresoLabels;
      if (!Array.isArray(labels) || !labels.length || !this.rangeIsValid) {
        return null;
      }
      const start = Math.floor(Number(this.rangeStart)) - 1;
      const end = Math.floor(Number(this.rangeEnd));
      const slice = labels.slice(start, end);
      return {
        Plataforma: slice.filter((l) => l === "Plataforma").length,
        App: slice.filter((l) => l === "App").length,
        Vitality: slice.filter((l) => l === "Vitality").length,
      };
    },
    analysisRows() {
      const a = this.analysis;
      if (!a) return [];
      const after = a.leadsAfterSendByDay || {};
      const base = a.baselineLeadsByDay || {};
      const dayKeys = Object.keys(after).sort();
      // Backend usa fechas distintas: post D+0..D+3 son 11..14 y baseline D-1..D-4 son 10..07.
      // No usar base[postKey]; emparejar por índice: baseline del día (postD0 - (i+1)) civil.
      const addDaysYmd = (ymd, deltaDays) => {
        const parts = String(ymd || "")
          .split("-")
          .map((x) => parseInt(x, 10));
        if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return "";
        const dt = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
        dt.setUTCDate(dt.getUTCDate() + deltaDays);
        const y = dt.getUTCFullYear();
        const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
        const d = String(dt.getUTCDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      };
      const post0 = dayKeys[0] || "";
      return dayKeys.map((k, i) => {
        const baselineDayKey = post0 ? addDaysYmd(post0, -(i + 1)) : "";
        const baseline =
          baselineDayKey &&
          Object.prototype.hasOwnProperty.call(base, baselineDayKey)
            ? base[baselineDayKey]
            : 0;
        return {
          dayKey: k,
          after: after[k] ?? 0,
          baselineDayKey,
          baseline: baseline ?? 0,
        };
      });
    },
    snapshotTableRows() {
      const list = Array.isArray(this.snapshots) ? [...this.snapshots] : [];
      return list
        .sort((a, b) => String(a.id || "").localeCompare(String(b.id || "")))
        .map((s) => ({
          id: s.id,
          dayOffset: s.dayOffset ?? "—",
          updated: this.prettySnapshotTime(s.updatedAt),
          created: this.prettySnapshotTime(s.createdAt),
        }));
    },
    testSendParsedEmails() {
      const raw = String(this.testSendEmailsRaw || "");
      const parts = raw
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const out = [];
      const seen = new Set();
      for (const p of parts) {
        const key = p.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(p);
      }
      return out.slice(0, 5);
    },
    canTestSend() {
      const list = this.testSendParsedEmails;
      if (!list.length || list.length > 5) return false;
      // Validación básica en UI; el backend vuelve a validar (incl. tope duro).
      return list.every((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    },
  },
  methods: {
    /** Etiqueta "desde–hasta / total" para historial de campañas. */
    campaignExcelRangeLabel(c) {
      if (!c || c.rangeStart == null || c.rangeEnd == null) return "";
      const a = Number(c.rangeStart);
      const b = Number(c.rangeEnd);
      const t = Number(c.totalSortedRecipients);
      if (!Number.isFinite(a) || !Number.isFinite(b)) return "";
      if (Number.isFinite(t) && t >= 1) return `${a}–${b} / ${t}`;
      return `${a}–${b}`;
    },
    prettyBytes(bytes) {
      const b = Number(bytes || 0);
      if (!b) return "0 B";
      const units = ["B", "KB", "MB", "GB"];
      const i = Math.min(
        units.length - 1,
        Math.floor(Math.log(b) / Math.log(1024)),
      );
      const val = b / Math.pow(1024, i);
      return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
    },
    resetResult() {
      this.error = "";
      this.result = null;
    },
    prettyDate(iso) {
      if (!iso) return "-";
      try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return String(iso);
        return d.toLocaleString("es-EC", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return String(iso);
      }
    },
    prettySnapshotTime(raw) {
      if (raw === undefined || raw === null) return "—";
      try {
        if (typeof raw === "string") return this.prettyDate(raw);
        if (raw instanceof Date) return this.prettyDate(raw.toISOString());
        const sec = raw._seconds ?? raw.seconds;
        if (sec != null && Number.isFinite(Number(sec))) {
          return this.prettyDate(new Date(Number(sec) * 1000).toISOString());
        }
      } catch {
        // ignore
      }
      return "—";
    },
    statusBadgeClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "sent") return "bg-success";
      if (s === "failed") return "bg-danger";
      if (s === "processing") return "bg-warning text-dark";
      return "bg-secondary";
    },
    onFileChange(e) {
      this.resetResult();
      this.excelStats = null;
      this.excelStatsError = "";
      this.excelStatsLoading = false;
      const file = e.target.files && e.target.files[0];
      if (!file) {
        this.excelBase64 = "";
        this.fileName = "";
        this.fileSize = 0;
        this.rangeStart = 1;
        this.rangeEnd = 1;
        return;
      }
      this.fileName = file.name;
      this.fileSize = file.size;

      const reader = new FileReader();
      reader.onload = () => {
        this.excelBase64 = String(reader.result || "");
        this.fetchExcelStats();
      };
      reader.onerror = () => {
        this.excelBase64 = "";
        this.error = "No se pudo leer el archivo";
      };
      reader.readAsDataURL(file);
    },
    async fetchExcelStats() {
      if (!this.excelBase64) return;
      this.excelStatsLoading = true;
      this.excelStatsError = "";
      this.excelStats = null;
      try {
        const res = await this.$https.post("/mailing/excelStats", {
          tokenAuth: this.tokenAuth,
          role: this.role,
          excelBase64: this.excelBase64,
        });
        if (res.data && res.data.code === 200) {
          this.excelStats = res.data;
          const n = Number(res.data.totalSortedRecipients || 0);
          this.rangeStart = 1;
          this.rangeEnd = n > 0 ? Math.min(100, n) : 1;
        } else {
          this.excelStatsError =
            res.data?.error || "No se pudo analizar el Excel";
        }
      } catch (err) {
        this.excelStatsError =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Error al analizar el Excel";
      } finally {
        this.excelStatsLoading = false;
      }
    },
    async send() {
      this.resetResult();
      if (!this.excelBase64) {
        this.error = "Selecciona un Excel primero";
        return;
      }
      if (!String(this.subject || "").trim()) {
        this.error = "El asunto es requerido";
        return;
      }
      if (!this.rangeIsValid) {
        this.error =
          "Rango inválido: revisa Desde/Hasta (1-based, hasta el total de la lista).";
        return;
      }
      this.sending = true;
      try {
        const res = await this.$https.post("/mailing/sendFromExcel", {
          tokenAuth: this.tokenAuth,
          role: this.role,
          template: this.template,
          subject: String(this.subject || "").trim(),
          excelBase64: this.excelBase64,
          rangeStart: Math.floor(Number(this.rangeStart)),
          rangeEnd: Math.floor(Number(this.rangeEnd)),
        });
        if (res.data && res.data.code === 200) {
          this.result = res.data;
          const n = Number(res.data.totalRecipients || 0);
          const msg =
            n === 0
              ? "Rango procesado: 0 correos enviados (revisa email, notificaciones o duplicados en el rango)."
              : "Mailing enviado correctamente";
          this.$toast?.success?.(msg, {
            position: "top-right",
            timeout: 3000,
          });
          // refresh campaigns list
          this.fetchCampaigns();
        } else {
          this.error = res.data?.error || "No se pudo enviar el mailing";
          this.$toast?.error?.(this.error, {
            position: "top-right",
            timeout: 5000,
          });
        }
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Error enviando mailing";
        this.error = msg;
        this.$toast?.error?.(msg, { position: "top-right", timeout: 5000 });
        // Si backend devuelve mailingLogId en error, lo mostramos igual
        if (err?.response?.data?.mailingLogId) {
          this.result = err.response.data;
        }
      } finally {
        this.sending = false;
      }
    },
    async copyLog() {
      const id = this.result?.mailingLogId;
      if (!id) return;
      try {
        await navigator.clipboard.writeText(String(id));
        this.$toast?.success?.("Log ID copiado", { timeout: 2000 });
      } catch (e) {
        // fallback simple
        alert(String(id));
      }
    },
    async fetchCampaigns() {
      this.campaignsError = "";
      this.loadingCampaigns = true;
      try {
        const res = await this.$https.post("/mailing/list", {
          tokenAuth: this.tokenAuth,
          role: this.role,
          limit: 25,
        });
        if (res.data && res.data.code === 200) {
          this.campaigns = Array.isArray(res.data.campaigns)
            ? res.data.campaigns
            : [];
        } else {
          this.campaignsError = res.data?.error || "No se pudo cargar campañas";
        }
      } catch (err) {
        this.campaignsError =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Error cargando campañas";
      } finally {
        this.loadingCampaigns = false;
      }
    },
    async openAnalysis(c) {
      this.analysisOpen = true;
      this.selectedCampaign = c;
      this.analysisError = "";
      this.analysis = null;
      this.snapshots = [];
      this.latestSnapshotId = null;
      this.latestSnapshotAt = null;
      this.analysisLoading = true;
      try {
        const res = await this.$https.post(
          `/mailing/${encodeURIComponent(c.id)}/stored`,
          {
            tokenAuth: this.tokenAuth,
            role: this.role,
          },
        );
        if (res.data && res.data.code === 200) {
          this.analysis = res.data.analysis || null;
          this.snapshots = Array.isArray(res.data.snapshots)
            ? res.data.snapshots
            : [];
          this.latestSnapshotId = res.data.latestSnapshotId || null;
          this.latestSnapshotAt = res.data.latestSnapshotAt || null;
        } else {
          this.analysisError = res.data?.error || "No se pudo obtener análisis";
        }
      } catch (err) {
        this.analysisError =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Error obteniendo análisis";
      } finally {
        this.analysisLoading = false;
      }
    },
    async recalculateAnalysis() {
      const c = this.selectedCampaign;
      if (!c?.id) return;
      this.analysisError = "";
      this.recalculating = true;
      try {
        const res = await this.$https.post(
          `/mailing/${encodeURIComponent(c.id)}/analysis`,
          {
            tokenAuth: this.tokenAuth,
            role: this.role,
          },
        );
        if (res.data && res.data.code === 200) {
          this.analysis = res.data.analysis || null;
          this.snapshots = Array.isArray(res.data.snapshots)
            ? res.data.snapshots
            : [];
          this.latestSnapshotId = res.data.latestSnapshotId || null;
          this.latestSnapshotAt = res.data.latestSnapshotAt || null;
        } else {
          this.analysisError =
            res.data?.error || "No se pudo recalcular análisis";
        }
      } catch (err) {
        this.analysisError =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Error recalculando análisis";
      } finally {
        this.recalculating = false;
      }
    },
    closeAnalysis() {
      this.analysisOpen = false;
      this.selectedCampaign = null;
    },
    switchToAutoTab() {
      this.mailingTab = "auto";
      this.fetchAutoCampaigns();
    },
    autoFilterLabel(ft) {
      const map = {
        all: "Toda la base",
        referred_success: "Referidores con éxito",
        referred_only_failed: "Referidores solo sin éxito",
        never_referred: "Nunca han referido",
        testing: "Testing (equipo)",
      };
      return map[ft] || ft || "-";
    },
    autoProgressPct(c) {
      const sent = Number(c?.stats?.totalSent || 0);
      const pending = Number(c?.stats?.totalPending || 0);
      const denom = sent + pending;
      if (!denom) return 0;
      return Math.min(100, Math.round((sent / denom) * 100));
    },
    autoProgressLabel(c) {
      const sent = Number(c?.stats?.totalSent || 0);
      const pending = Number(c?.stats?.totalPending || 0);
      return `${sent} / ${sent + pending}`;
    },
    autoStatusBadgeClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "active") return "bg-success";
      if (s === "paused") return "bg-warning text-dark";
      if (s === "completed") return "bg-secondary";
      return "bg-light text-dark";
    },
    async fetchAutoCampaigns() {
      this.autoCampaignsError = "";
      this.loadingAutoCampaigns = true;
      try {
        const res = await this.$https.post("/mailingCampaigns/list", {
          tokenAuth: this.tokenAuth,
          role: this.role,
          limit: 50,
        });
        if (res.data && res.data.code === 200) {
          this.autoCampaigns = Array.isArray(res.data.campaigns)
            ? res.data.campaigns
            : [];
        } else {
          this.autoCampaignsError =
            res.data?.error || "No se pudo cargar campañas automáticas";
        }
      } catch (err) {
        this.autoCampaignsError =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Error cargando campañas automáticas";
      } finally {
        this.loadingAutoCampaigns = false;
      }
    },
    async createAutoCampaign() {
      this.autoCreateError = "";
      this.autoCreating = true;
      try {
        const res = await this.$https.post(
          "/mailingCampaigns/create",
          {
            tokenAuth: this.tokenAuth,
            role: this.role,
            filterType: this.autoFilterType,
            template: this.autoTemplate,
            subject: this.autoSubject || "Saludsa",
          },
          { timeout: 60000 },
        );
        if (res.data && res.data.code === 200) {
          const snapshotPending = !!res.data.snapshotPending;
          this.$toast?.success?.(
            snapshotPending
              ? "Campaña creada. Construyendo audiencia…"
              : "Campaña creada",
            { timeout: 4000 },
          );
          await this.fetchAutoCampaigns();
          if (res.data.campaignId) {
            const created =
              this.autoCampaigns.find((c) => c.id === res.data.campaignId) ||
              res.data.campaign ||
              { id: res.data.campaignId };
            await this.openAutoDetail(created);
            if (snapshotPending) {
              // Petición aparte: en Cloud Run el trabajo tras res.json() se corta.
              this.kickOffInitialSnapshot(created);
              this.pollAutoDetailUntilSnapshot(created);
            }
          }
        } else {
          this.autoCreateError =
            res.data?.error || "No se pudo crear la campaña";
        }
      } catch (err) {
        this.autoCreateError =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Error creando campaña";
      } finally {
        this.autoCreating = false;
      }
    },
    async kickOffInitialSnapshot(c) {
      if (!c?.id) return;
      try {
        const res = await this.$https.post(
          `/mailingCampaigns/${encodeURIComponent(c.id)}/snapshotNow`,
          { tokenAuth: this.tokenAuth, role: this.role },
          { timeout: 300000 },
        );
        if (res.data && res.data.code === 200) {
          const row = Array.isArray(res.data.results)
            ? res.data.results[0]
            : null;
          if (row) this.autoSnapshotResult = row;
          if (this.selectedAutoCampaign?.id === c.id) {
            await this.openAutoDetail(c, { silent: true });
          }
          await this.fetchAutoCampaigns();
        }
      } catch (err) {
        console.warn("[kickOffInitialSnapshot]", err);
        if (this.selectedAutoCampaign?.id === c.id) {
          this.autoDetailError =
            err?.response?.data?.error ||
            err?.message ||
            "El snapshot inicial falló o tardó demasiado. Usa «Ejecutar snapshot ahora».";
        }
      }
    },
    async pollAutoDetailUntilSnapshot(c, attempts = 36, intervalMs = 5000) {
      if (!c?.id) return;
      for (let i = 0; i < attempts; i++) {
        await new Promise((r) => setTimeout(r, intervalMs));
        if (!this.autoDetailOpen || this.selectedAutoCampaign?.id !== c.id) {
          return;
        }
        await this.openAutoDetail(c, { silent: true });
        await this.fetchAutoCampaigns();
        if (this.autoDetail?.campaign?.stats?.lastSnapshotAt) {
          this.$toast?.success?.("Audiencia lista", { timeout: 2500 });
          return;
        }
      }
    },
    async pauseAutoCampaign(c) {
      if (!c?.id) return;
      this.autoStatusBusyId = c.id;
      try {
        const res = await this.$https.post(
          `/mailingCampaigns/${encodeURIComponent(c.id)}/pause`,
          { tokenAuth: this.tokenAuth, role: this.role },
        );
        if (res.data && res.data.code === 200) {
          await this.fetchAutoCampaigns();
        } else {
          this.autoCampaignsError =
            res.data?.error || "No se pudo pausar la campaña";
        }
      } catch (err) {
        this.autoCampaignsError =
          err?.response?.data?.error ||
          err?.message ||
          "Error pausando campaña";
      } finally {
        this.autoStatusBusyId = "";
      }
    },
    async resumeAutoCampaign(c) {
      if (!c?.id) return;
      this.autoStatusBusyId = c.id;
      try {
        const res = await this.$https.post(
          `/mailingCampaigns/${encodeURIComponent(c.id)}/resume`,
          { tokenAuth: this.tokenAuth, role: this.role },
        );
        if (res.data && res.data.code === 200) {
          await this.fetchAutoCampaigns();
        } else {
          this.autoCampaignsError =
            res.data?.error || "No se pudo reanudar la campaña";
        }
      } catch (err) {
        this.autoCampaignsError =
          err?.response?.data?.error ||
          err?.message ||
          "Error reanudando campaña";
      } finally {
        this.autoStatusBusyId = "";
      }
    },
    async openAutoDetail(c, { silent = false } = {}) {
      this.autoDetailOpen = true;
      this.selectedAutoCampaign = c;
      this.autoDetailError = "";
      if (!silent) {
        this.autoDetail = null;
        this.autoSnapshotResult = null;
        this.autoDetailLoading = true;
      }
      try {
        const res = await this.$https.post(
          `/mailingCampaigns/${encodeURIComponent(c.id)}/detail`,
          { tokenAuth: this.tokenAuth, role: this.role },
        );
        if (res.data && res.data.code === 200) {
          this.autoDetail = {
            campaign: res.data.campaign || null,
            recipientCounts: res.data.recipientCounts || null,
          };
        } else {
          this.autoDetailError =
            res.data?.error || "No se pudo obtener el detalle";
        }
      } catch (err) {
        this.autoDetailError =
          err?.response?.data?.error ||
          err?.message ||
          "Error obteniendo detalle";
      } finally {
        if (!silent) this.autoDetailLoading = false;
      }
    },
    closeAutoDetail() {
      this.autoDetailOpen = false;
      this.selectedAutoCampaign = null;
      this.autoDetail = null;
      this.autoSnapshotResult = null;
    },
    async runAutoSnapshotNow() {
      const c = this.selectedAutoCampaign;
      if (!c?.id) return;
      this.autoDetailError = "";
      this.autoSnapshotBusy = true;
      this.autoSnapshotResult = null;
      try {
        const res = await this.$https.post(
          `/mailingCampaigns/${encodeURIComponent(c.id)}/snapshotNow`,
          { tokenAuth: this.tokenAuth, role: this.role },
          { timeout: 300000 },
        );
        if (res.data && res.data.code === 200) {
          const row = Array.isArray(res.data.results)
            ? res.data.results[0]
            : null;
          this.autoSnapshotResult = row || { added: 0 };
          await this.openAutoDetail(c);
          await this.fetchAutoCampaigns();
        } else {
          this.autoDetailError =
            res.data?.error || "No se pudo ejecutar el snapshot";
        }
      } catch (err) {
        this.autoDetailError =
          err?.response?.data?.error ||
          err?.message ||
          "Error ejecutando snapshot";
      } finally {
        this.autoSnapshotBusy = false;
      }
    },
    async confirmAndSendTest() {
      this.testSendError = "";
      this.testSendResult = null;
      const emails = this.testSendParsedEmails;
      if (!emails.length) {
        this.testSendError = "Indica al menos 1 email";
        return;
      }
      if (emails.length > 5) {
        this.testSendError = "Máximo 5 emails";
        return;
      }
      const ok = window.confirm(
        `Vas a enviar ${emails.length} correo(s) real(es) a:\n\n${emails.join(
          "\n",
        )}\n\n¿Confirmas?`,
      );
      if (!ok) return;

      this.testSendBusy = true;
      try {
        const res = await this.$https.post("/mailingCampaigns/testSend", {
          tokenAuth: this.tokenAuth,
          role: this.role,
          emails,
          template: this.testSendTemplate,
          subject: this.testSendSubject || "Saludsa [PRUEBA]",
        });
        if (res.data && res.data.code === 200) {
          this.testSendResult = res.data;
        } else {
          this.testSendError =
            res.data?.error ||
            res.data?.detail ||
            "No se pudo enviar la prueba";
        }
      } catch (err) {
        const data = err?.response?.data;
        this.testSendError =
          data?.error ||
          data?.detail ||
          err?.message ||
          "Error enviando prueba";
        if (Array.isArray(data?.invalidEmails) && data.invalidEmails.length) {
          this.testSendError += ` — inválidos: ${data.invalidEmails.join(", ")}`;
        }
      } finally {
        this.testSendBusy = false;
      }
    },
  },
  mounted() {
    this.fetchCampaigns();
  },
};
</script>

<style scoped>
.mailing-container {
  padding: 0;
}

.tone-alert {
  border: 1px solid transparent;
  border-radius: 0.75rem;
  padding: 0.9rem 1rem;
  background: #fff;
}
.tone-alert__title {
  font-weight: 700;
  font-size: 0.95rem;
  margin-bottom: 0.15rem;
  color: #1f2937;
}
.tone-alert__body {
  font-size: 0.9rem;
  color: #374151;
}
.tone-alert--info {
  background: #f3f7ff;
  border-color: #cfe0ff;
}
.tone-alert--success {
  background: #f3fbf7;
  border-color: #cbeedc;
}
.tone-alert--warning {
  background: #fff7ed;
  border-color: #fde2c0;
}
.tone-alert--error {
  background: #fff5f5;
  border-color: #ffd1d1;
}

.preview-shell {
  border: 1px solid #e9ecef;
  border-radius: 0.75rem;
  background: #ffffff;
  overflow: hidden;
  max-width: 600px;
}

.preview-img {
  display: block;
  width: 100%;
  height: auto;
}

.preview-iframe {
  display: block;
  width: 100%;
  min-height: 920px;
  border: 0;
}

.preview-footer {
  background: #f4f5f7;
  color: #6b7280;
  font-size: 12px;
  line-height: 18px;
  padding: 16px 18px;
  text-align: center;
}

.preview-unsub {
  color: #6b7280;
  text-decoration: underline;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
}

.stat-icon {
  font-size: 2rem;
  margin-right: 1rem;
  width: 60px;
  text-align: center;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0;
  color: #2d3748;
}

.stat-label {
  font-size: 0.875rem;
  color: #718096;
  margin: 0.25rem 0 0 0;
}

.response-pre {
  max-height: 340px;
  overflow: auto;
  background: #0b1020;
  color: #e6edf3;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-size: 12px;
  line-height: 1.35;
}

.analysis-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  z-index: 1000;
}

.analysis-modal {
  width: 100%;
  max-width: 900px;
  max-height: 85vh;
  overflow: auto;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}

.impact-td {
  padding: 10px 12px;
  vertical-align: middle;
}

.response-pre--compact {
  max-height: 220px;
}

.lead-creators-table {
  max-height: 380px;
  overflow: auto;
}

.mailing-test-send {
  background: #fffbeb;
  border-color: #f59e0b !important;
  border-width: 1px;
  border-style: solid;
}
</style>
